import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Prescription, PrescriptionDataTransformer } from "@/types/prescription";
import PrescriptionForm from "./PrescriptionForm";
import { 
  X, 
  AlertTriangle, 
  User,
  Shield
} from "lucide-react";

interface PatientData {
  name: string;
  age?: number;
  gender?: string;
  phone?: string;
  allergies?: string[];
  chronicConditions?: string[];
  activeMedications?: string[];
  lastVisit?: string;
  latestVitals?: {
    bp?: string;
    weight?: string;
    temp?: string;
  };
  criticalAlerts: string[];
}

interface EditPrescriptionModalProps {
  open: boolean;
  prescription: Prescription;
  patientId?: string;
  onClose: () => void;
  onUpdated?: () => void;
}

export default function EditPrescriptionModal({ 
  open, 
  prescription, 
  patientId: fallbackPatientId, 
  onClose, 
  onUpdated 
}: EditPrescriptionModalProps) {
  const [isClosing, setIsClosing] = useState(false);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (open) {
      setIsClosing(false);
    }
  }, [open]);

  const handleClose = () => {
    setIsClosing(true);
    onClose();
  };

  const handleSaved = () => {
    onUpdated?.();
    onClose();
  };

  // Validate prescription data
  const isValidPrescription = prescription && 
    (prescription.id || prescription.date) &&
    (prescription.patient_id || fallbackPatientId);

  const formatDate = (dateString?: Date | string) => {
    if (!dateString) return "Unknown date";
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return "Invalid date";
    }
  };

  const getPatientId = (): string => {
    const patientId = prescription.patient_id || fallbackPatientId || "";
    
    if (!patientId) {
      console.error("❌ No patient ID found in EditPrescriptionModal!");
    }
    
    return patientId;
  };

  const loadExistingPrescriptionData = () => {
    const patientId = prescription.patient_id || fallbackPatientId || "";
    return PrescriptionDataTransformer.toFormData(prescription, patientId);
  };

  // Enhanced patient data extraction from API response or prescription data
  const getPatientDataFromAPI = (): PatientData => {
    // In a real implementation, this would fetch from API based on prescription.patient_id
    // For now, we'll use the prescription data and some reasonable defaults
    return {
      name: prescription.patient_name || "Patient",
      age: undefined, // Would come from API
      gender: undefined, // Would come from API
      phone: undefined, // Would come from API
      allergies: [], // Would come from API
      chronicConditions: [], // Would come from API
      activeMedications: [], // Would come from API
      lastVisit: prescription.date ? formatDate(prescription.date) : undefined,
      latestVitals: prescription.vitals ? {
        bp: prescription.vitals.bp,
        weight: prescription.vitals.weight,
        temp: prescription.vitals.temp
      } : {},
      criticalAlerts: [] // Would be computed based on patient conditions
    };
  };

  const patientData = getPatientDataFromAPI();

  // Error state with better UX
  if (!isValidPrescription) {
    console.error("❌ Invalid prescription data:", { prescription, fallbackPatientId });
    
    return (
      <Dialog open={open} onOpenChange={() => {}}>
        <DialogContent className="max-w-md rounded-xl shadow-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-medical-vitals">
              <div className="p-2 bg-medical-vitals/10 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-medical-vitals" />
              </div>
              <span className="text-lg font-semibold">Cannot Edit Prescription</span>
            </DialogTitle>
          </DialogHeader>
          
          <Alert className="border-medical-vitals/20 bg-medical-vitals/5">
            <AlertTriangle className="h-4 w-4 text-medical-vitals" />
            <AlertDescription className="text-medical-vitals/80">
              This prescription is missing required information. Please contact support.
              <br />
              <small className="text-xs">
                Debug: ID={prescription?.id}, Date={prescription?.date}, PatientID={prescription?.patient_id || fallbackPatientId}
              </small>
            </AlertDescription>
          </Alert>
          
          <div className="flex justify-end pt-4">
            <Button variant="outline" onClick={handleClose}>
              <X className="w-4 h-4 mr-2" />
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent
        className="w-full max-w-screen-xl h-[95vh] flex flex-col rounded-lg shadow-2xl"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()} 
        showClose={false}
      >
        {/* Header - Professional Design */}
        <div className="flex-shrink-0 modal-header-professional px-6 py-4">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-2 bg-muted rounded-lg">
                  <User className="w-5 h-5 text-muted-foreground" />
                </div>
                <div>
                  <DialogTitle className="text-xl font-bold flex items-center gap-3 text-foreground">
                    {patientData.name || "Patient"} - Edit Prescription
                    {patientData.criticalAlerts.length > 0 && (
                      <span className="bg-destructive text-destructive-foreground px-2 py-1 rounded-full text-xs font-medium animate-pulse">
                        <Shield className="w-3 h-3 mr-1 inline" />
                        Alert
                      </span>
                    )}
                  </DialogTitle>
                  <p className="text-muted-foreground text-sm font-medium">
                    Age: {patientData.age} • Last visit: {patientData.lastVisit} • {formatDate(prescription.date)}
                  </p>
                </div>
              </div>
              
              <Button
                variant="outline"
                size="icon"
                onClick={handleClose}
                className="h-8 w-8"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </DialogHeader>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex overflow-hidden">
          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="flex-1 overflow-auto">
              <div className="p-4">
                <PrescriptionForm
                  mode="edit"
                  patientId={prescription.patient_id}
                  initialData={loadExistingPrescriptionData()}
                  onSaved={handleSaved}
                  onCancel={onClose}
                  showStickyBar={true}
                />
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}